import React, { useState, useEffect, useRef, useMemo } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { LayoutDashboard, Users, Microscope, ClipboardList, FileSpreadsheet, History, MessageSquareText, Plus, X, Stethoscope, FlaskConical, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sidebar as SidebarComponent, SidebarHeader, SidebarContent } from '@/components/ui/sidebar'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useMediaQuery } from '@/hooks/use-media-query'
import { Logo } from '@/components/common/logo'
import { Separator } from '@/components/ui/separator'
import { user_role } from '@/entities/user'
import { useAuthStore } from '@/store/auth'
import { useChatStore } from '@/store/chat'
import { ConversationCard } from '@/components/cards/conversation-card'
import { externalRoutes } from '@/utils/routes-util'
import { CreditsStatus } from '@/components/common/credit-status'
import { initMercadoPago } from '@mercadopago/sdk-react'
import { api } from '@/lib/axios'
import { CustomDialog } from '@/components/dialogs/custom-dialog'
import { HTTP_STATUS_CODE } from '@/constants/http'
import { useToast } from '@/hooks/use-toast'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Form, FormField, FormControl, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { useForm, useWatch } from 'react-hook-form'
import { generateCheckoutLinkSchema, GenerateCheckoutLinkSchema } from '@/entities/checkout'
import { zodResolver } from '@hookform/resolvers/zod'
import { showConfirm } from '@/lib/alerts'

interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string
  isOpen?: boolean
  onClose?: () => void
}

export function Sidebar({ className, isOpen, onClose }: SidebarProps) {
  const { user } = useAuthStore()
  const location = useLocation()
  const { toast } = useToast()
  const isMdAndDown = useMediaQuery('(max-width:1123px)')
  const sidebarRef = useRef<HTMLDivElement>(null)
  const [isShowingCreditsModal, setIsShowingCreditsModal] = useState<boolean>(false)
  const [checkoutLink, setCheckoutLink] = useState<string | undefined>(undefined)

  const CREDITS_RATE = 1

  const form = useForm<GenerateCheckoutLinkSchema>({
    resolver: zodResolver(generateCheckoutLinkSchema),
    defaultValues: {
      userRef: user?.ref,
      value: 10,
      amount: CREDITS_RATE
    }
  })

  const formValues = useWatch({ control: form.control })

  const _addCredits = async () => {
    const isValid = await form.trigger()
    if (!isValid) return

    const { isConfirmed } = await showConfirm('Deseja confirmar a compra de créditos?')
    if (!isConfirmed) return

    initMercadoPago(import.meta.env.VITE_MP_PUBLIC_KEY)

    try {
      const response = await api.post('/', {
        action: 'checkout',
        userRef: formValues.userRef,
        amount: Number(formValues.amount),
        value: Number(formValues.value)
      })

      if (response.data.statusCode == HTTP_STATUS_CODE.OK) {
        setCheckoutLink(response.data.body[0].url)
      } else {
        toast({
          variant: 'destructive',
          title: 'Ops ...',
          description: response?.data?.statusMessage || 'Não foi possível gerar o link de pagamento.'
        })
      }
    } catch (err) {
      console.error(err)
    }
  }

  const handleNavigation = (href: string) => {
    window.location.href = href
    if (isOpen && onClose) onClose()
  }

  function useOnClickOutside(ref: React.RefObject<HTMLElement>, handler: (event: MouseEvent | TouchEvent) => void) {
    useEffect(() => {
      const listener = (event: MouseEvent | TouchEvent) => {
        if (!ref.current || ref.current.contains(event.target as Node)) {
          return
        }
        handler(event)
      }

      document.addEventListener('mousedown', listener)
      document.addEventListener('touchstart', listener)
      return () => {
        document.removeEventListener('mousedown', listener)
        document.removeEventListener('touchstart', listener)
      }
    }, [ref, handler])
  }

  useOnClickOutside(sidebarRef, () => {
    if (isOpen && onClose) onClose()
  })

  useEffect(() => {
    form.setValue('amount', (formValues.value || 0) * CREDITS_RATE)
  }, [formValues.value])

  useEffect(() => {
    if (!isShowingCreditsModal) setCheckoutLink(undefined)
  }, [isShowingCreditsModal])

  useEffect(() => {
    if (checkoutLink) {
      window.open(checkoutLink, '_blank')
      setIsShowingCreditsModal(false)
    }
  }, [checkoutLink])

  if (externalRoutes.includes(location.pathname)) return <></>

  return (
    <>
      <SidebarComponent ref={sidebarRef} side="left" variant="sidebar" collapsible={!isOpen && isMdAndDown ? 'icon' : 'none'} className="min-h-screen z-30 overflow-y-auto !max-w-[290px] !min-w-[290px] fixed py-3 bg-card border-r border-border shadow-card p-3">
        <SidebarContent className="!max-w-[260px] !min-w-[250px]">
          <SidebarHeader>
            <div className="relative flex items-center justify-between py-2 pb-4 border-b border-border mb-2">
              <div onClick={() => handleNavigation('/chat')} className="cursor-pointer flex-1">
                <Logo size="md" showText />
              </div>
              {isOpen && isMdAndDown && (
                <button
                  onClick={() => onClose?.()}
                  className="ml-2 flex items-center justify-center h-9 w-9 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                  aria-label="Fechar menu"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          </SidebarHeader>

          <NavigationItems onNavigate={href => handleNavigation(href)} />

          <div className={cn('max-h-auto hyphens-auto fixed bottom-0 left-0 bg-card/95 backdrop-blur-xl !max-w-[290px] !min-w-[290px] border-t border-border', isMdAndDown && 'pb-4')}>
            <div className="rounded-lg bg-muted/40 w-full p-2 flex flex-col gap-y-2">
              <CreditsStatus remainingCredits={Number(((((user?.balance || 0) + (Number(user?.planBalance) || 0)) * 100) / 100).toFixed(2))} />

              <Button variant="default" className="w-full text-base font-semibold flex gap-x-2 hover-lift" size="sm" onClick={() => setIsShowingCreditsModal(true)}>
                <Plus className="size-4" />
                Comprar Créditos
              </Button>
            </div>
          </div>
        </SidebarContent>
      </SidebarComponent>

      {isShowingCreditsModal && (
        <CustomDialog
          open={isShowingCreditsModal}
          persistent={true}
          title="Comprar Créditos"
          description="Adicione créditos à sua conta para continuar usando os recursos"
          confirmationBeforeClose={false}
          onClose={() => {
            setIsShowingCreditsModal(false)
          }}
          className="min-w-[1000px]"
        >
          {!checkoutLink && (
            <Card className="max-w-xl mx-auto p-6 pt-4 rounded-2xl shadow-none border-none relative pb-16 w-full bg-transparent">
              <CardContent className="flex xsAndDown:flex-col gap-x-10 p-0">
                <Form {...form}>
                  <form
                    className="flex flex-col gap-6 w-full"
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        _addCredits()
                      }
                    }}
                  >
                    <FormField
                      control={form.control}
                      name="value"
                      render={({ field }) => (
                        <FormItem className="w-full">
                          <FormLabel className="text-foreground font-medium">Valor:</FormLabel>
                          <FormControl>
                            <div className="flex flex-col">
                              <div className="text-xs text-muted-foreground flex flex-row justify-start">
                                <span className="text-xs text-muted-foreground">(R$ 1,00 = {CREDITS_RATE} crédito)</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <div className="h-full bg-primary flex items-center justify-center rounded-l-md p-1 px-3">
                                  <span className="text-primary-foreground text-xl font-bold">R$</span>
                                </div>
                                <Input {...field} type="number" min={10} max={1000000} step={1} className="!text-center text-md rounded-l-none w-full" placeholder="Digite o valor" disabled={!!checkoutLink} />
                              </div>
                              <div className="text-xs text-muted-foreground flex flex-row justify-start">
                                <span className="text-xs text-muted-foreground">(Min. 10 créditos)</span>
                              </div>
                            </div>
                          </FormControl>

                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button type="button" className="mt-4 w-[calc(100%_-_16px)] absolute bottom-4 left-2" disabled={!!checkoutLink} onClick={_addCredits}>
                      Comprar
                    </Button>
                  </form>
                </Form>

                <div className="flex flex-col text-center my-2 flex-1 justify-center">
                  <div className="text-lg font-medium text-foreground">Você receberá:</div>
                  <div className="text-3xl font-bold text-primary truncate">
                    {formValues.amount} {`crédito${formValues.amount === 1 ? '' : 's'}`}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </CustomDialog>
      )}
    </>
  )
}

function NavigationItems({ onNavigate }: { onNavigate: (href: string) => void }) {
  const location = useLocation()
  const { user } = useAuthStore()
  const { conversations, createNewConversation } = useChatStore()
  const isMdAndDown = useMediaQuery('(max-width:1123px)')
  const [searchTerm, setSearchTerm] = useState('')

  const filteredConversations = useMemo(() => {
    if (!searchTerm.trim()) return conversations
    const term = searchTerm.toLowerCase().trim()
    return conversations.filter(c => {
      if (c.title?.toLowerCase().includes(term)) return true
      return c.messages?.some(m => m.content?.toLowerCase().includes(term))
    })
  }, [conversations, searchTerm])

  const handleNewChat = async () => {
    const newConversation = await createNewConversation()
    onNavigate(`/chat/${newConversation?.ref}`)
  }

  const navigation = [
    {
      id: 'chat',
      icon: MessageSquareText,
      name: 'Consultor de IA',
      visible: true,
      href: '/chat'
    },
    {
      id: 'exam-consultant',
      icon: Microscope,
      name: 'Consultor de Exames',
      description: 'Sugestões de protocolos e suplementações baseadas nos resultados',
      visible: false, // desativado temporariamente
      href: '/exam-consultant'
    },
    {
      id: 'signs-symptoms',
      icon: Stethoscope,
      name: 'Sinais e Sintomas',
      description: 'Mapeie sinais e sintomas e gere análise clínica com a Nina',
      visible: true,
      href: '/signs-symptoms'
    },
    {
      id: 'exam-analysis',
      icon: FlaskConical,
      name: 'Análise de Exames',
      description: 'Preencha marcadores laboratoriais e gere análise clínica com a Nina',
      visible: true,
      href: '/exam-analysis'
    },
    {
      id: 'anamnesis',
      icon: ClipboardList,
      name: 'Modelos de Anamnese',
      description: 'Templates prontos por perfil',
      visible: true,
      href: '/anamnesis'
    },
    {
      id: 'exam-library',
      icon: FileSpreadsheet,
      name: 'Biblioteca de Exames',
      description: 'Exames recomendados por caso',
      visible: true,
      href: '/exam-library'
    },
    {
      id: 'dashboard',
      icon: LayoutDashboard,
      name: 'Dashboard',
      visible: user?.role == user_role.ADMIN || user?.role == user_role.MODERATOR,
      href: '/dashboard'
    },
    {
      id: 'user-management',
      icon: Users,
      name: 'Usuários',
      visible: user?.role == user_role.ADMIN || user?.role == user_role.MODERATOR,
      href: '/users'
    }
  ]

  return (
    <nav className="space-y-1 flex flex-col items-center pb-[150px]">
      <div className="w-full">
        <TooltipProvider delayDuration={0}>
          {navigation
            ?.filter(nav => nav.visible)
            ?.map(item => (
              <Tooltip key={item.href}>
                <TooltipTrigger asChild>
                  <NavLink
                    to={item.href}
                    onClick={() => onNavigate(item.href)}
                    className={cn(
                      'h-11 flex w-full items-center my-1 rounded-xl transition-all justify-start text-sm font-medium relative',
                      location.pathname.includes(item.href)
                        ? 'bg-primary/20 text-coentro font-semibold'
                        : 'text-foreground/80 hover:bg-muted hover:text-foreground'
                    )}
                  >
                    {location.pathname.includes(item.href) && (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r-full bg-primary" aria-hidden />
                    )}
                    <div className="flex items-center justify-center px-6">
                      <item.icon className={cn('h-4 w-4 flex-shrink-0', location.pathname.includes(item.href) ? 'text-coentro' : 'text-muted-foreground')} />
                      <span className="ml-3">{item.name}</span>
                    </div>
                  </NavLink>
                </TooltipTrigger>
                <TooltipContent className="hidden" side="right">
                  {item.name}
                </TooltipContent>
              </Tooltip>
            ))}
        </TooltipProvider>
      </div>

      <div className="w-full px-2">
        <Separator className="bg-border/40" />
      </div>

      <div className="flex w-full flex-col justify-center items-center">
        <div className="text-foreground text-base flex items-center">
          <History className="h-5 w-5 text-muted-foreground" />
          <span className="px-1 text-foreground text-base flex items-center font-medium">Histórico de Conversas</span>
        </div>

        <div className="w-full px-2 my-3">
          <Button size="sm" variant="outline" onClick={handleNewChat} className="w-full rounded-lg">
            <Plus className="h-4 w-4 mr-3" />
            Nova Conversa
          </Button>
        </div>

        <div className="relative px-2 mb-2 w-full">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar conversas…"
            className="w-full pl-9 pr-3 py-1.5 text-sm rounded-md bg-muted/50 border border-border focus:outline-none focus:ring-1 focus:ring-primary placeholder:text-muted-foreground"
          />
        </div>

        <div className="w-full overflow-y-auto scrollbar-thin scrollbar-thumb-primary/40 scrollbar-track-transparent scrollbar-thumb-rounded-full">
          {searchTerm && filteredConversations.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              Nenhuma conversa encontrada para "{searchTerm}"
            </div>
          )}
          <ConversationCard items={filteredConversations} />
        </div>
      </div>
    </nav>
  )
}
